import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Building2, 
  Plus, 
  Search,
  MapPin,
  Home,
  Warehouse,
  FileText,
  Users,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  Check,
  X,
  CheckSquare,
  Square,
  Upload
} from "lucide-react";
import { ImportAssetsModal } from "@/components/assets/ImportAssetsModal";
import { ExportAllButton } from "@/components/assets/ExportAllButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";

const AssetsPage = () => {
  const { token, user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [filterLeaseStatus, setFilterLeaseStatus] = useState("all");
  const [deleteAsset, setDeleteAsset] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingValueId, setEditingValueId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (error) {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleDelete = async () => {
    if (!deleteAsset) return;
    
    try {
      await axios.delete(`${API}/assets/${deleteAsset.asset_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Asset deleted successfully");
      fetchAssets();
    } catch (error) {
      toast.error("Failed to delete asset");
    } finally {
      setDeleteAsset(null);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    let deleted = 0;
    for (const assetId of selectedAssets) {
      try {
        await axios.delete(`${API}/assets/${assetId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch (error) {
        // continue with others
      }
    }
    toast.success(`${deleted} asset(s) deleted`);
    setSelectedAssets(new Set());
    setBulkDeleting(false);
    setShowBulkDeleteDialog(false);
    fetchAssets();
  };

  const toggleSelect = (assetId) => {
    setSelectedAssets(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAssets.size === filteredAssets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(filteredAssets.map(a => a.asset_id)));
    }
  };

  const handleQuickValueSave = async (assetId) => {
    try {
      await axios.put(`${API}/assets/${assetId}`, {
        estimated_value: editingValue ? parseFloat(editingValue) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Value updated");
      setEditingValueId(null);
      fetchAssets();
    } catch (error) {
      toast.error("Failed to update value");
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "Not valued";
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getAssetIcon = (type) => {
    switch (type) {
      case 'land': return MapPin;
      case 'residential': return Home;
      case 'commercial': return Warehouse;
      default: return Building2;
    }
  };

  const getAssetImage = (type, index) => {
    const images = {
      residential: "https://images.unsplash.com/photo-1757439402268-1da284675170?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjB2aWxsYSUyMGV4dGVyaW9yJTIwbmlnaHR8ZW58MHx8fHwxNzY5MjgyNjk0fDA&ixlib=rb-4.1.0&q=85",
      commercial: "https://images.unsplash.com/photo-1761157995927-54b10202f92f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBtb2Rlcm4lMjBnbGFzc3xlbnwwfHx8fDE3NjkyODI3MDF8MA&ixlib=rb-4.1.0&q=85",
      land: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800"
    };
    return images[type] || images.land;
  };

  // Extract unique states for filter
  const stateOptions = [...new Set(assets.map(a => a.state).filter(Boolean))].sort();

  const filteredAssets = assets.filter(asset => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = asset.name.toLowerCase().includes(term) ||
                          asset.state?.toLowerCase().includes(term) ||
                          asset.lga?.toLowerCase().includes(term) ||
                          asset.plot_number?.toLowerCase().includes(term) ||
                          asset.unit_number?.toLowerCase().includes(term) ||
                          asset.block_number?.toLowerCase().includes(term);
    const matchesType = filterType === "all" || asset.asset_type === filterType;
    const matchesState = filterState === "all" || asset.state === filterState;
    const matchesLease = filterLeaseStatus === "all" || asset.lease_status === filterLeaseStatus;
    return matchesSearch && matchesType && matchesState && matchesLease;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="assets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Assets</h1>
            <p className="text-slate-400 mt-1">Manage your property portfolio</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ExportAllButton token={token} testId="export-all-btn-assets" />
            <Button variant="outline" className="btn-secondary" onClick={() => setShowImportModal(true)} data-testid="import-assets-btn">
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Link to="/assets/new">
              <Button className="btn-primary" data-testid="add-new-asset-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </Link>
          </div>
        </div>

        <ImportAssetsModal open={showImportModal} onOpenChange={setShowImportModal} token={token} onImported={fetchAssets} />

        {/* Bulk Actions Bar */}
        {selectedAssets.size > 0 && (
          <div className="flex items-center gap-4 p-3 rounded-xl bg-primary/10 border border-primary/30" data-testid="bulk-actions-bar">
            <span className="text-primary font-medium text-sm">{selectedAssets.size} selected</span>
            <Button
              size="sm"
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => setShowBulkDeleteDialog(true)}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-400 hover:text-white"
              onClick={() => setSelectedAssets(new Set())}
              data-testid="clear-selection-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-center">
          {filteredAssets.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors px-2"
              data-testid="select-all-btn"
            >
              {selectedAssets.size === filteredAssets.length && filteredAssets.length > 0 ? (
                <CheckSquare className="w-5 h-5 text-primary" />
              ) : (
                <Square className="w-5 h-5" />
              )}
              <span className="text-sm">All</span>
            </button>
          )}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search assets, plot numbers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-default"
              data-testid="search-assets-input"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-40 input-default" data-testid="filter-type-select">
              <SelectValue placeholder="Asset Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="land">Land</SelectItem>
              <SelectItem value="residential">Residential</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
            </SelectContent>
          </Select>
          {stateOptions.length > 0 && (
            <Select value={filterState} onValueChange={setFilterState}>
              <SelectTrigger className="w-full sm:w-40 input-default" data-testid="filter-state-select">
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {stateOptions.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filterLeaseStatus} onValueChange={setFilterLeaseStatus}>
            <SelectTrigger className="w-full sm:w-40 input-default" data-testid="filter-lease-status-select">
              <SelectValue placeholder="Lease Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="leased">Leased</SelectItem>
              <SelectItem value="vacant">Vacant</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Assets Grid */}
        {filteredAssets.length === 0 ? (
          <Card className="card-dashboard">
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No assets found</h3>
              <p className="text-slate-400 mb-6">
                {searchTerm || filterType !== "all" || filterState !== "all" || filterLeaseStatus !== "all"
                  ? "Try adjusting your search or filters"
                  : "Start by adding your first asset to the portfolio"
                }
              </p>
              {!searchTerm && filterType === "all" && filterState === "all" && filterLeaseStatus === "all" && (
                <Link to="/assets/new">
                  <Button className="btn-primary" data-testid="empty-add-asset-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Asset
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAssets.map((asset) => {
              const Icon = getAssetIcon(asset.asset_type);
              const isSelected = selectedAssets.has(asset.asset_id);
              const isOwner = asset.user_id === user?.user_id;
              return (
                <Card key={asset.asset_id} className={`card-dashboard overflow-hidden group ${isSelected ? 'ring-2 ring-primary' : ''}`} data-testid={`asset-card-${asset.asset_id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={asset.image_url || getAssetImage(asset.asset_type)}
                      alt={asset.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent"></div>
                    
                    {/* Select Checkbox — only for owned assets */}
                    {isOwner && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelect(asset.asset_id); }}
                        className="absolute top-4 left-4 z-10"
                        data-testid={`select-asset-${asset.asset_id}`}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-6 h-6 text-primary drop-shadow-lg" />
                        ) : (
                          <Square className="w-6 h-6 text-white/60 hover:text-white drop-shadow-lg" />
                        )}
                      </button>
                    )}
                    
                    {/* Type Badge */}
                    <Badge className={`absolute top-4 ${isOwner ? 'left-14' : 'left-4'} bg-slate-900/80 backdrop-blur-sm text-white border-slate-700 capitalize`}>
                      <Icon className="w-3 h-3 mr-1" />
                      {asset.asset_type}
                    </Badge>

                    {/* Team Badge — for assets from team owner */}
                    {!isOwner && (
                      <Badge className="absolute top-12 left-4 bg-violet-500/20 backdrop-blur-sm text-violet-300 border-violet-500/30">
                        <Users className="w-3 h-3 mr-1" />
                        Team
                      </Badge>
                    )}
                    
                    {/* Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-sm hover:bg-slate-800"
                          data-testid={`asset-menu-${asset.asset_id}`}
                        >
                          <MoreVertical className="w-4 h-4 text-white" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                        <Link to={`/assets/${asset.asset_id}`}>
                          <DropdownMenuItem className="text-slate-300 hover:text-white focus:text-white">
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                        </Link>
                        {isOwner && (
                          <Link to={`/assets/${asset.asset_id}`}>
                            <DropdownMenuItem className="text-slate-300 hover:text-white focus:text-white">
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit Asset
                            </DropdownMenuItem>
                          </Link>
                        )}
                        {isOwner && (
                          <DropdownMenuItem 
                            className="text-red-400 hover:text-red-300 focus:text-red-300"
                            onClick={() => setDeleteAsset(asset)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <CardContent className="p-4">
                    <Link to={`/assets/${asset.asset_id}`}>
                      <h3 className="text-lg font-semibold text-white hover:text-primary transition-colors">
                        {asset.name}
                      </h3>
                    </Link>
                    <p className="text-slate-400 text-sm mt-1 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {asset.lga}, {asset.state}
                    </p>
                    {(asset.plot_number || asset.unit_number || asset.block_number) && (
                      <p className="text-slate-500 text-xs mt-1 font-mono">
                        {asset.plot_number && `Plot: ${asset.plot_number}`}
                        {asset.unit_number && `Unit: ${asset.unit_number}`}
                        {asset.block_number && `Block: ${asset.block_number}`}
                      </p>
                    )}
                    
                    <div className="mt-4 pt-4 border-t border-slate-800">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-xs text-slate-500">Estimated Value</p>
                          {editingValueId === asset.asset_id ? (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-slate-500 text-sm">₦</span>
                              <Input
                                type="number"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                className="h-8 w-28 input-default text-sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleQuickValueSave(asset.asset_id);
                                  if (e.key === "Escape") setEditingValueId(null);
                                }}
                                data-testid={`quick-value-input-${asset.asset_id}`}
                              />
                              <Button size="icon" className="h-7 w-7 bg-primary hover:bg-primary/80" onClick={() => handleQuickValueSave(asset.asset_id)} data-testid={`quick-value-save-${asset.asset_id}`}>
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={() => setEditingValueId(null)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <p
                              className="text-lg font-semibold text-white mono cursor-pointer hover:text-primary transition-colors group/val"
                              onClick={() => { setEditingValueId(asset.asset_id); setEditingValue(asset.estimated_value || ""); }}
                              data-testid={`quick-value-${asset.asset_id}`}
                              title="Click to edit value"
                            >
                              {formatCurrency(asset.estimated_value)}
                              <Pencil className="w-3 h-3 inline ml-2 opacity-0 group-hover/val:opacity-100 transition-opacity" />
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={asset.documentation_status === 'complete' 
                            ? 'status-complete' 
                            : 'status-pending'
                          }>
                            <FileText className="w-3 h-3 mr-1" />
                            Docs
                          </Badge>
                          <Badge className={asset.lease_status === 'leased' 
                            ? 'status-complete' 
                            : 'status-missing'
                          }>
                            <Users className="w-3 h-3 mr-1" />
                            {asset.lease_status === 'leased' ? 'Leased' : 'Vacant'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteAsset} onOpenChange={() => setDeleteAsset(null)}>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Asset</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to delete "{deleteAsset?.name}"? This action cannot be undone and will also delete all related documents and leases.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <AlertDialogContent className="bg-slate-900 border-slate-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete {selectedAssets.size} Asset(s)</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to delete {selectedAssets.size} selected asset(s)? This action cannot be undone and will also delete all related documents and leases.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={bulkDeleting}
                data-testid="confirm-bulk-delete-btn"
              >
                {bulkDeleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : `Delete ${selectedAssets.size} Asset(s)`}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AssetsPage;
